import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowUpRight, Building2, PlaySquare, Film, TrendingUp, AlertCircle } from 'lucide-react'
import { listSponsors, listAds, listFilmsForAdAssignment } from '@/lib/api/advertiser'

interface DashboardStats {
  totalSponsors: number
  totalAds: number
  activeAds: number
  filmsWithAds: number
  filmsTotal: number
}

export function AdvertiserDashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    async function load() {
      try {
        const [sponsorsRes, adsRes, filmsRes] = await Promise.all([
          listSponsors({ limit: 200 }),
          listAds({ limit: 200 }),
          listFilmsForAdAssignment({ limit: 200 }),
        ])
        if (cancelled) return

        const activeAds = adsRes.items.filter(
          (a) => a.status === 'active' || !a.status,
        ).length
        const filmsWithAds = filmsRes.items.filter(
          (f) => f.prerollAdId || f.displayAdId,
        ).length

        setStats({
          totalSponsors: sponsorsRes.totalCount ?? sponsorsRes.items.length,
          totalAds: adsRes.totalCount ?? adsRes.items.length,
          activeAds,
          filmsWithAds,
          filmsTotal: filmsRes.totalCount ?? filmsRes.items.length,
        })
      } catch (err) {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to load advertiser stats')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">
            Advertiser dashboard
          </h1>
          <p className="mt-1 text-sm text-gray-400">
            Manage sponsors, ad creatives, and placements across Binyuma TV content.
          </p>
        </div>
        <Link
          to="/advertiser/sponsors"
          className="inline-flex items-center gap-2 text-sm text-[#D72638] hover:text-red-400 transition-colors"
        >
          Manage sponsors
          <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-lg border border-red-700 bg-red-900/20 px-4 py-3 flex items-start gap-3 text-sm text-red-300">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error} — some data may be unavailable until the backend endpoints are live.</span>
        </div>
      )}

      {/* KPI cards */}
      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Total sponsors"
          value={loading ? '—' : String(stats?.totalSponsors ?? 0)}
          sub="Registered brand partners"
          icon={<Building2 className="w-4 h-4 text-[#D72638]" />}
          href="/advertiser/sponsors"
        />
        <StatCard
          label="Ad creatives"
          value={loading ? '—' : String(stats?.totalAds ?? 0)}
          sub={`${stats?.activeAds ?? 0} currently active`}
          icon={<PlaySquare className="w-4 h-4 text-[#D72638]" />}
          href="/advertiser/ads"
        />
        <StatCard
          label="Films with ads"
          value={loading ? '—' : String(stats?.filmsWithAds ?? 0)}
          sub={`of ${stats?.filmsTotal ?? '—'} total films`}
          icon={<Film className="w-4 h-4 text-[#D72638]" />}
          href="/advertiser/placements"
        />
        <StatCard
          label="Reach potential"
          value="—"
          sub="Impression analytics coming soon"
          icon={<TrendingUp className="w-4 h-4 text-[#D72638]" />}
          href="/advertiser/analytics"
        />
      </section>

      {/* Quick actions */}
      <section className="grid gap-4 md:grid-cols-3">
        <QuickAction
          title="Add a sponsor"
          description="Register a new brand partner and set their tier and verification status."
          href="/advertiser/sponsors"
          cta="Go to sponsors"
        />
        <QuickAction
          title="Upload an ad creative"
          description="Create a pre-roll video or display image ad and link it to a sponsor."
          href="/advertiser/ads"
          cta="Go to ad creatives"
        />
        <QuickAction
          title="Assign ads to films"
          description="Choose which films carry pre-roll or display ads from your active inventory."
          href="/advertiser/placements"
          cta="Go to placements"
        />
      </section>

      {/* Info banner */}
      <div className="rounded-xl border border-gray-800 bg-gradient-to-br from-gray-900/60 to-black p-5 text-sm text-gray-400 space-y-1">
        <p className="font-medium text-white text-base">About the advertiser portal</p>
        <p>
          This portal lets the Binyuma TV team manage brand partnerships end-to-end — from onboarding
          sponsors and uploading ad creatives to assigning them to specific films and tracking
          impression performance. It mirrors the workflow described in the{' '}
          <Link to="/admin/roadmap" className="text-[#D72638] hover:underline">
            product roadmap
          </Link>{' '}
          (Phase 4: Sponsor Workflows).
        </p>
        <p>
          Analytics and impression tracking will be enabled once the ad-serving backend is live.
          Until then, all CRUD operations for sponsors, ads, and placements are fully functional.
        </p>
      </div>
    </div>
  )
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  sub,
  icon,
  href,
}: {
  label: string
  value: string
  sub: string
  icon: React.ReactNode
  href: string
}) {
  return (
    <Link
      to={href}
      className="rounded-xl border border-gray-800 bg-gradient-to-br from-gray-900/80 to-black px-4 py-4 hover:border-[#D72638]/40 transition-colors group"
    >
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
        {icon}
      </div>
      <p className="text-2xl font-semibold text-white mb-1 group-hover:text-[#D72638] transition-colors">
        {value}
      </p>
      <p className="text-xs text-gray-400">{sub}</p>
    </Link>
  )
}

function QuickAction({
  title,
  description,
  href,
  cta,
}: {
  title: string
  description: string
  href: string
  cta: string
}) {
  return (
    <div className="rounded-xl border border-gray-800 bg-gradient-to-br from-gray-900/60 to-black p-5 flex flex-col gap-3">
      <div>
        <p className="text-sm font-semibold text-white mb-1">{title}</p>
        <p className="text-xs text-gray-400 leading-relaxed">{description}</p>
      </div>
      <Link
        to={href}
        className="mt-auto inline-flex items-center gap-1 text-xs font-medium text-[#D72638] hover:text-red-400 transition-colors"
      >
        {cta}
        <ArrowUpRight className="w-3 h-3" />
      </Link>
    </div>
  )
}
